import { useRef } from 'react';

/**
 * Pins the item that was current when something (e.g. a dialog) opened, and
 * keeps returning it - in its latest version from `items` - until it closes.
 *
 * Useful when `current` is derived from state that can change underneath an
 * open dialog (list refresh, index shift), which would otherwise swap or
 * unmount the item being edited.
 *
 * - closed: returns `current` and forgets the pinned item,
 * - open: returns the pinned item, refreshed from `items` by `getKey`, or the
 *   last known version when it is no longer listed.
 *
 * `getKey` must identify an item uniquely. Items without a key (`undefined`)
 * are never matched, so the pinned item is kept as last known instead.
 */
export function usePinnedWhileOpen<T, K extends string | number>(
  isOpen: boolean,
  current: T | undefined,
  items: readonly T[],
  getKey: (item: T) => K | undefined
): T | undefined {
  const pinnedRef = useRef<T | undefined>(undefined);

  if (!isOpen) {
    pinnedRef.current = undefined;
    return current;
  }

  const pinned = pinnedRef.current;
  // Writing the ref during render is safe here: the result depends only on
  // the inputs, so repeated renders (StrictMode, concurrent) give the same value.
  pinnedRef.current = pinned ? findLatest(items, pinned, getKey) : current;
  return pinnedRef.current;
}

function findLatest<T, K>(
  items: readonly T[],
  pinned: T,
  getKey: (item: T) => K | undefined
): T {
  const pinnedKey = getKey(pinned);
  if (pinnedKey === undefined) {
    return pinned;
  }
  return items.find((item) => getKey(item) === pinnedKey) ?? pinned;
}
